import { reactive } from 'vue';
import { io } from 'socket.io-client';
import { tokenStore, signalUnauthorized } from './http.js';
import { перевестиОтказ } from '../i18n/server.js';

/* Клиент событий цеха. Через него приходит то, что должно появляться
   на экране само: состояния станков, выпуск, движение заказов, аварии.
   Запросы человека идут по HTTP (http.js). */

const URL = import.meta.env.VITE_WS_URL || undefined;   // undefined = тот же origin

/* Отказы рукопожатия, которые повтором не лечатся. Тексты приходят с сервера
   (services/realtime.js) — держим их рядом, чтобы расхождение было заметно. */
const АВТОРИЗАЦИЯ = ['Сессия истекла', 'Недействительный токен', 'Требуется авторизация',
  'Учётная запись недоступна', 'Учётные данные изменены'];

export const socketState = reactive({
  connected: false,
  error: null,
  subscribed: [],
});

let socket = null;
const handlers = new Map();
/* Подписки помним сами: после переподключения их нужно восстановить.
   Сервер умеет восстанавливать состояние после коротких разрывов, но при
   длинном обрыве комнаты теряются. */
let wanted = new Set();

/* Сессия кончилась. Гасим сокет и отдаём решение туда же, куда его отдаёт
   отказ HTTP: хранилище сессии уведёт человека на вход и объяснит причину. */
function сессияКончилась(reason) {
  socketState.connected = false;
  socket?.io?.reconnection(false);
  signalUnauthorized(reason);
}

export function connectSocket({ token: явныйКлюч } = {}) {
  const token = явныйКлюч || tokenStore.get();
  if (!token) return null;
  if (socket?.connected) return socket;

  socket?.close();
  /* Транспорт не форсируем. Socket.IO сам начинает с polling и повышает
     соединение до WebSocket, когда это удаётся. Попытка сразу открыть
     WebSocket ломается на обратных прокси (в разработке — на прокси Vite,
     в цехе — на корпоративном), и каждое переподключение сыпало ошибками
     в консоль, хотя связь держалась на polling. */
  socket = io(URL, {
    auth: { token },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  socket.on('connect', () => {
    socketState.connected = true;
    socketState.error = null;
    if (wanted.size) resubscribe();
  });

  socket.on('disconnect', (reason) => {
    socketState.connected = false;
    socketState.subscribed = [];
    if (reason !== 'io client disconnect') socketState.error = перевестиОтказ('Связь с сервером потеряна');
  });

  /* Сервер предупреждает перед разрывом по истечении токена. Без этого
     клиент видел бы просто обрыв и показывал «Связь потеряна» — человек
     решил бы, что дело в сети, и стал бы ждать. */
  socket.on('session:expired', (p) => {
    socketState.error = перевестиОтказ(p?.reason || 'Сессия истекла');
    сессияКончилась(p?.reason || 'Сессия истекла');
  });

  socket.on('connect_error', (e) => {
    socketState.connected = false;
    /* Показываем переведённым, а проверку АВТОРИЗАЦИЯ ниже оставляем
       на исходном тексте: она сверяется с русскими подстроками. */
    socketState.error = перевестиОтказ(e.message);

    /* Отказ авторизации повтором не лечится.

       Сокет переподключается сам с растущей паузой — и при истёкшем токене
       делал бы это вечно, каждый раз получая тот же отказ. Токен в цехе живёт
       смену, а планшет не перезагружают: к утру это тысячи бесполезных попыток
       и экран, на котором ничего не происходит. Останавливаемся и уводим
       человека на вход — тем же путём, что и при отказе HTTP. */
    if (АВТОРИЗАЦИЯ.some((s) => String(e.message).includes(s))) {
      socket.io.reconnection(false);
      сессияКончилась(e.message);
    }
  });

  // Переадресуем все известные события в подписчиков
  for (const event of handlers.keys()) bindEvent(event);

  return socket;
}

function bindEvent(event) {
  socket?.off(event);
  socket?.on(event, (payload) => {
    for (const fn of handlers.get(event) || []) {
      try { fn(payload); } catch (e) { console.error(`[ws] обработчик ${event} упал:`, e); }
    }
  });
}

/* Подписка на событие. Возвращает функцию отписки — её удобно вызывать
   из onUnmounted компонента. */
export function on(event, fn) {
  if (!handlers.has(event)) {
    handlers.set(event, new Set());
    if (socket) bindEvent(event);
  }
  handlers.get(event).add(fn);
  return () => handlers.get(event)?.delete(fn);
}

/* Подписка на узел дерева: станок, участок или цех. Сервер сам разошлёт
   события всего поддерева и проверит область видимости. */
export function subscribe(nodes) {
  const list = (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean).map(String);
  for (const n of list) wanted.add(n);
  if (!socket?.connected) return Promise.resolve({ ok: false, pending: true });

  return new Promise((resolve) => {
    socket.emit('subscribe', { nodes: [...wanted] }, (ack) => {
      socketState.subscribed = ack?.subscribed || [];
      /* Узел, отклонённый по области видимости, убираем из желаемых —
         иначе будем просить его при каждом переподключении. */
      if (ack?.ok) {
        const granted = new Set(socketState.subscribed);
        for (const n of [...wanted]) if (!granted.has(n)) wanted.delete(n);
      }
      resolve(ack || { ok: false });
    });
  });
}

export function unsubscribe(nodes) {
  const list = (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean).map(String);
  for (const n of list) wanted.delete(n);
  socket?.emit('unsubscribe', { nodes: list }, () => {});
}

function resubscribe() {
  if (!wanted.size) return;
  socket.emit('subscribe', { nodes: [...wanted] }, (ack) => {
    socketState.subscribed = ack?.subscribed || [];
  });
}

export function disconnectSocket() {
  wanted = new Set();
  socketState.subscribed = [];
  socketState.connected = false;
  socket?.close();
  socket = null;
}

export const EVENTS = {
  equipmentState: 'equipment:state',
  productionReport: 'production:report',
  orderStatus: 'order:status',
  operationStatus: 'operation:status',
  alert: 'alert',
  tooling: 'tooling:changed',
};
